import { useState, type ChangeEvent } from 'react';
import { postMailcomImportTxt, type MailcomImportTxtResponse } from '../../api/endpoints.js';
import { useApiMutation } from '../../api/hooks.js';
import { Banner, Button, Modal } from '../../ui/index.js';

/**
 * Phase N HOTFIX — V1 file-picker import (parity with
 * legacy/v1-archived-2026-05/.../ParentMails.jsx handleImport:237-248 + file
 * input:705-716). The operator picks ONE .txt file (parent rows
 * `email:password:refresh_token`); the whole file is sent in a SINGLE POST
 * `/admin/providers/mailcom/import`, the backend batch-upserts every line and
 * fires bootstrap-renewal in the background. No per-row requests, no textarea,
 * no alias/bundle tabs. Aliases are produced by the renewal turn, never listed
 * by the operator.
 *
 * Audit F20 (2026-09-17) — the mailbox-unit ("rambler logic") providers have NO
 * alias system: the import line format differs (2-field `email:password`, AOL
 * 4-field `login:web_password:phone:app_password`), nothing is minted, and the
 * backend ignores `aliasDomain` for them, so the modal shows their own contract
 * and hides the alias-domain input.
 */

/**
 * Mailbox-unit providers: the mailbox itself is the sellable unit and the sale
 * right is per (mailbox, site). Mirrors the API bundles registered with
 * `siteScopedRights: true` (apps/api/src/server.ts) and
 * SITE_SCOPED_UNIT_DOMAIN_DEFAULTS (apps/api/src/routes/admin/site-scoped-import-lines.ts).
 */
export const MAILBOX_UNIT_PROVIDERS: ReadonlySet<string> = new Set([
  'rambler',
  'aol',
  'webde',
  'libero',
  'tiscaliit',
  'tiscalicz',
  'sina',
  'sinavip',
  'gazetapl',
  'rediffmail',
  'tonline',
]);

export interface BulkImportModalProps {
  readonly providerId: string;
  /**
   * Human/domain label for the provider this modal is bound to (e.g. 'mail.com'
   * or 'gmx.com'). Drives the alias-domain placeholder + the body/summary copy
   * so the GMX page never shows a 'mail.com' literal. Default keeps the mailcom
   * mount byte-identical.
   */
  readonly providerLabel?: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCompleted: () => void;
}

export function BulkImportModal({
  providerId,
  providerLabel = 'mail.com',
  open,
  onClose,
  onCompleted,
}: BulkImportModalProps): JSX.Element | null {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="TXT Import" width={620} testId="mailcom-bulk-import-modal">
      <BulkImportBody
        providerId={providerId}
        providerLabel={providerLabel}
        onClose={onClose}
        onCompleted={onCompleted}
      />
    </Modal>
  );
}

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(String(e.target?.result ?? ''));
    reader.onerror = () => reject(new Error('Dosya okunamadı'));
    reader.readAsText(file);
  });
}

function countLines(text: string): number {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith('#')).length;
}

function BulkImportBody({
  providerId,
  providerLabel,
  onClose,
  onCompleted,
}: {
  readonly providerId: string;
  readonly providerLabel: string;
  readonly onClose: () => void;
  readonly onCompleted: () => void;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [fileInfo, setFileInfo] = useState('');
  const [domain, setDomain] = useState('');
  const [summary, setSummary] = useState<MailcomImportTxtResponse | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  const importMut = useApiMutation((o, params: { text: string; aliasDomain?: string }) =>
    postMailcomImportTxt(o, params, providerId),
  );
  const running = importMut.state.kind === 'running';
  const mailboxUnit = MAILBOX_UNIT_PROVIDERS.has(providerId);

  async function onFileChange(ev: ChangeEvent<HTMLInputElement>): Promise<void> {
    const f = ev.target.files?.[0] ?? null;
    setFile(f);
    setSummary(null);
    setReadError(null);
    if (!f) {
      setFileInfo('');
      return;
    }
    try {
      const text = await readFileText(f);
      setFileInfo(`${f.name} — ${countLines(text)} satır`);
    } catch {
      setFileInfo(`${f.name}`);
    }
  }

  async function handleImport(): Promise<void> {
    if (!file) return;
    setReadError(null);
    let text: string;
    try {
      text = await readFileText(file);
    } catch (err) {
      setReadError(err instanceof Error ? err.message : 'Dosya okunamadı');
      return;
    }
    if (!text.trim()) {
      setReadError('Dosya boş');
      return;
    }
    const res = await importMut.run({
      text,
      aliasDomain: mailboxUnit ? undefined : domain.trim() || undefined,
    });
    if (res.kind === 'ok') {
      setSummary(res.data);
      onCompleted();
    } else {
      setReadError(res.kind === 'error' ? res.reason : `unexpected:${res.kind}`);
    }
  }

  return (
    <div>
      {mailboxUnit ? (
        // Mailbox-unit providers (rambler logic): no alias system. The mailbox
        // itself is the sellable unit and the sale right is per (mailbox,
        // customer site), so there is nothing to mint and no alias list to
        // curate. The line format follows the backend parser exactly.
        <div className="t-tertiary t-small" style={{ margin: '0 0 12px' }} data-testid="bulk-import-format">
          {providerId === 'aol' ? (
            <p style={{ margin: '0 0 6px' }}>
              Her satır <code>login:web_password:phone:app_password</code> formatında (4 alan) bir parent
              hesabıdır (ayraç <code>:</code> veya <code>|</code>). IMAP girişinde yalnız 4. alan olan{' '}
              <strong>uygulama şifresi (app password)</strong> kullanılır — 16 harf, aradaki boşluklar yok
              sayılır; web şifresi ve telefon yalnız şifreli kasada saklanır. Web şifresi <code>:</code>{' '}
              içerebilir (satır iki uçtan ayrıştırılır).
            </p>
          ) : (
            <p style={{ margin: '0 0 6px' }}>
              Her satır <code>email:password</code> formatında bir parent hesabıdır (ayraç <code>:</code>{' '}
              veya <code>|</code>; yalnız ilk ayraçtan bölünür, şifre <code>:</code> içerebilir). Şifre,
              posta sunucusu girişinde kullanılan şifredir.
            </p>
          )}
          <p style={{ margin: 0 }}>
            {providerLabel} hesaplarında <strong>alias üretilmez</strong>: satılabilir birim
            mailbox&apos;ın kendisidir (adres {providerLabel} alan adında olmalıdır) ve satış hakkı
            (mailbox, müşteri sitesi) bazında tutulur — aynı mailbox farklı bir site için tekrar
            satılabilir, mail geldiği site ise herkese kapanır. Mailbox, kimlik doğrulaması geçince satışa
            açılır. Tüm dosya tek istekte gönderilir; boş satırlar ve <code>#</code> ile başlayan yorum
            satırları yok sayılır. Credential bilgileri AES-256-GCM ile şifreli kasaya kaydedilir;
            ekranda, log&apos;da veya outbox event&apos;inde tekrar görünmez.
          </p>
        </div>
      ) : (
        <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>
          Her satır <code>email:password:refresh_token</code> formatında bir parent hesabıdır (ayraç{' '}
          <code>:</code> veya <code>|</code>). Tüm dosya tek istekte gönderilir; alias&apos;lar{' '}
          {providerLabel} üzerinde sistem tarafından üretilir — operatör alias listesi girmez. Boş
          satırlar ve <code>#</code> ile başlayan yorum satırları yok sayılır. Credential bilgileri
          AES-256-GCM ile şifreli kasaya kaydedilir; ekranda, log&apos;da veya outbox event&apos;inde tekrar
          görünmez.
        </p>
      )}

      <div style={{ marginBottom: 12 }}>
        <input
          type="file"
          accept=".txt,text/plain"
          onChange={onFileChange}
          disabled={running}
          aria-label="parent TXT dosyası"
          data-testid="bulk-import-file-input"
        />
        {fileInfo ? (
          <p className="t-small" style={{ color: 'var(--success, #16a34a)', margin: '6px 0 0' }} data-testid="bulk-import-file-info">
            {fileInfo}
          </p>
        ) : (
          <p className="t-tertiary t-small" style={{ margin: '6px 0 0' }}>Dosya seçilmedi</p>
        )}
      </div>

      {mailboxUnit ? null : (
        // Alias providers only — the import route ignores `aliasDomain` for
        // mailbox-unit providers (the unit domain is the address's own).
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          <label htmlFor="bulk-import-domain" className="label" style={{ minWidth: 180 }}>
            Alias Domain (opsiyonel)
          </label>
          <input
            id="bulk-import-domain"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder={providerLabel}
            disabled={running}
            spellCheck={false}
            autoComplete="off"
            data-testid="bulk-import-domain"
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <Button type="button" variant="ghost" onClick={onClose} disabled={running}>
          İptal
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleImport}
          loading={running}
          disabled={!file || running}
          data-testid="bulk-import-submit"
        >
          Import Et
        </Button>
      </div>

      {readError ? (
        <div style={{ marginTop: 12 }} data-testid="bulk-import-error">
          <Banner tone="danger" title="Import başarısız">
            {readError}
          </Banner>
        </div>
      ) : null}

      {summary ? (
        <div style={{ marginTop: 12 }} data-testid="bulk-import-summary">
          <Banner tone={summary.errors > 0 ? 'warn' : 'success'} title="İçe aktarma sonucu">
            {summary.imported} eklendi, {summary.skipped} atlandı, {summary.errors} hata.{' '}
            {mailboxUnit
              ? 'Her mailbox tek satılabilir birim olarak eklenir (alias üretilmez); kimlik doğrulaması geçince satışa açılır.'
              : `Alias'lar ${providerLabel} üzerinde sistem tarafından üretilir.`}
            {summary.errorDetails && summary.errorDetails.length > 0 ? (
              <ul style={{ margin: '8px 0 0', paddingLeft: 22 }} data-testid="bulk-import-error-rows">
                {summary.errorDetails.map((er, idx) => (
                  <li key={`${er.line}-${idx}`}>
                    Satır {er.line || '—'}: {er.error}
                  </li>
                ))}
              </ul>
            ) : null}
          </Banner>
        </div>
      ) : null}
    </div>
  );
}
